import { useState } from 'react';
import { ActionIcon, Box, Group, Loader, Text, TextInput, UnstyledButton } from '@mantine/core';
import { CornerDownLeft, Sparkles, Undo2 } from 'lucide-react';
import type { ReactElement } from 'react';

interface MegaGridAssistBarProps {
    placeholder: string;
    busy: boolean;
    narrative: string | null;
    error: string | null;
    canRevert: boolean;
    onAsk: (prompt: string) => void;
    onRevert: () => void;
}

/**
 * The grid's natural-language command bar. Pure UI: the host wires the brain
 * via MegaGrid's `assist` prop; this renders the input, busy state, the plan
 * narrative, and a one-step revert.
 */
export default function MegaGridAssistBar({ placeholder, busy, narrative, error, canRevert, onAsk, onRevert }: MegaGridAssistBarProps): ReactElement {
    const [prompt, setPrompt] = useState('');

    const submit = (): void => {
        const trimmed = prompt.trim();
        if (trimmed.length === 0 || busy) return;
        onAsk(trimmed);
        setPrompt('');
    };

    return (
        <Box px="sm" py={6} style={{ borderBottom: '1px solid var(--bm-color-border-subtle)', background: 'var(--bm-color-surface-alt)' }}>
            <TextInput
                size="xs"
                value={prompt}
                onChange={(e) => setPrompt(e.currentTarget.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') submit();
                }}
                placeholder={placeholder}
                disabled={busy}
                leftSection={busy ? <Loader size={13} /> : <Sparkles size={13} />}
                rightSection={
                    <ActionIcon size="sm" variant="subtle" onClick={submit} disabled={busy || prompt.trim().length === 0} aria-label="Apply">
                        <CornerDownLeft size={13} />
                    </ActionIcon>
                }
            />
            {(narrative !== null || error !== null) && (
                <Group gap="xs" mt={5} wrap="nowrap" align="flex-start">
                    <Text size="xs" c={error !== null ? 'red' : 'dimmed'} style={{ flex: 1, lineHeight: 1.45 }}>
                        {error ?? narrative}
                    </Text>
                    {canRevert && error === null && (
                        <UnstyledButton onClick={onRevert} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Undo2 size={11} color="var(--bm-color-text-dimmed)" />
                            <Text size="xs" c="dimmed" fw={600} style={{ whiteSpace: 'nowrap' }}>
                                Revert
                            </Text>
                        </UnstyledButton>
                    )}
                </Group>
            )}
        </Box>
    );
}
